import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@mui/material';
import Liabilities from 'app/pages/loanApplication/tabs/financialInfo/UpdateTableModal/Content/SectionContent/Liabilities';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LiabilitiesType } from 'constants/loanApplication';
import { liabilitiesTypesOptions } from 'constants/options';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { closeDialogAction } from 'store/global/actions';
import { getBorrowersReoAction } from 'store/loanApplications/actions';
import { selectIsRefinanceLoanPurposeFromApplication } from 'store/loanApplications/selectors';

import { FINANCIAL_INFO_VIEWS_PATHS, NEW_RECORD_DATA } from '../../../../FinancialInfo.constants';
import { FinancialInfoTestIds } from '../../../../FinancialInfo.testIds';
import { CONTENT_DATA } from '../../Content.constants';

const cancelSx = { marginRight: '8px' };

const LiabilityForm = ({ formRef, isLoading, setIsLoading, onUpdateForm }) => {
  const dispatch = useDispatch();
  const [recordConfig, setRecordConfig] = useState(null);

  const { applicationId, entityId: borrowerId } = useParams();

  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);

  const initialFormValues = useMemo(
    () => ({
      [NEW_RECORD_DATA.id]: {
        type: liabilitiesTypesOptions.find(({ value }) => value === LiabilitiesType.LIABILITY),
      },
    }),
    [],
  );

  const {
    handleChange,
    form,
    setForm,
    setInForm,
    setSchema,
    setValidationErrors,
    validationErrors,
    validateField,
    isFormChanged,
    validateForm,
  } = useForm(initialFormValues);

  const { schema } = useMemo(
    () =>
      recordConfig
        ? getConfigInfo({ applicationData: {}, config: recordConfig, isNestedForm: true })
        : {},
    [recordConfig],
  );

  useEffect(() => {
    if (schema) {
      setSchema(schema);
    }
  }, [setSchema, schema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const { sendDataAction, normalizeDataToSend, tabId } =
        CONTENT_DATA[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES];

      setIsLoading(true);

      const normalizedDataToSend = normalizeDataToSend({
        dataToNormalize: form,
        isRefinanceFlow,
      });

      return dispatch(
        sendDataAction({
          isMortgageClicked: true,
          applicationId,
          ...normalizedDataToSend,
          requestMetadata: {
            tabId,
          },
        }),
      )
        .then(({ payload }) => {
          const [newLiability] = _.orderBy(payload.liabilities, 'orderNumber', 'desc');
          onUpdateForm(newLiability);
        })
        .then(() => dispatch(getBorrowersReoAction({ applicationId, isMortgageClicked: true })))
        .then(() => dispatch(closeDialogAction()))
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [validateForm, isRefinanceFlow, dispatch, setIsLoading, applicationId, form, onUpdateForm],
  );

  return (
    <div className="flex flex-col w-full gap-24">
      <Liabilities
        isMortgageClicked
        isNewRecord
        applicationId={applicationId}
        borrowerId={borrowerId}
        form={form}
        formRef={formRef}
        handleChange={handleChange}
        initialFormValues={initialFormValues}
        isLoading={isLoading}
        recordId={NEW_RECORD_DATA.id}
        setForm={setForm}
        setInForm={setInForm}
        setRecordConfig={setRecordConfig}
        setValidationErrors={setValidationErrors}
        validateField={validateField}
        validationErrors={validationErrors}
      />

      <div className="flex justify-end w-full">
        <Button
          data-testid={FinancialInfoTestIds.LIABILITY_FORM_CANCEL_BUTTON}
          sx={cancelSx}
          variant="line"
          onClick={null}
        >
          Cancel
        </Button>

        <Button
          data-testid={FinancialInfoTestIds.LIABILITY_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading}
          variant="contained"
          onClick={onSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

LiabilityForm.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export default withLoading(LiabilityForm);
